import "@uppy/core/dist/style.min.css";
import "@uppy/dashboard/dist/style.min.css";
import "@uppy/webcam/dist/style.min.css";
import "@uppy/image-editor/dist/style.min.css";

import { FileAccessTier } from "@outschool/gql-frontend-generated";
import { captureError } from "@outschool/platform";
import { Screen } from "@outschool/ui-utils";
import AwsS3 from "@uppy/aws-s3";
import Uppy from "@uppy/core";
import ImageEditor from "@uppy/image-editor";
import {
  Dashboard as DashboardReactComponent,
  useUppyEvent,
} from "@uppy/react";
import Webcam from "@uppy/webcam";
import React, { useState } from "react";

import { useGetS3FileUploadUrlMutation } from "../hooks";
import { OsUppyFile } from "../utils";

export type AllowedSources =
  | "all" // allows files from filesystem and built-in camera
  | "filesystem" // allows files from filesystem
  | "webcam" // allows uppy camera UI only - should be used only where filesystem and normal camera are not allowed
  | "video"; // allows video files

interface UppyComponentProps {
  maxNumberOfFiles?: number;
  maxFileSize?: number;
  width?: number;
  height?: number;
  allowedFileTypes?: string[];
  onFileAdded?: (file: OsUppyFile, uppy: Uppy) => void;
  onFilesUploaded?: (files: OsUppyFile[]) => void;
  onSuccess?: () => void;
  allowedSources?: AllowedSources;
  cropRatio?: number;
  usePrivateUpload?: boolean;
}

export const UppyComponent: React.FC<UppyComponentProps> = ({
  maxNumberOfFiles,
  maxFileSize,
  width = 750,
  height = 550,
  allowedFileTypes,
  onFileAdded,
  onFilesUploaded,
  onSuccess,
  allowedSources,
  cropRatio,
  usePrivateUpload = false,
}) => {
  const { getS3FileUploadUrl } = useGetS3FileUploadUrlMutation();

  const isMobile = Screen.useIsMobile();
  const isTablet = Screen.useIsTablet();
  const isMobileOrTablet = isMobile || isTablet;

  // none of the props should ever change, so the props aren't supposed to cause a re-render
  const [uppy] = useState(() => {
    const instance = new Uppy({
      restrictions: {
        allowedFileTypes,
        maxFileSize,
        maxNumberOfFiles,
      },
      id: "uppy-instance",
    })
      .use(ImageEditor, {
        quality: 1, // JPEG quality (0-1)
        actions: {
          revert: true,
          rotate: true,
          granularRotate: true,
          flip: true,
          zoomIn: true,
          zoomOut: true,
          cropSquare: true,
          cropWidescreen: true,
          cropWidescreenVertical: true,
        },
        cropperOptions: {
          viewMode: 1, // fit the whole image inside the frame
          aspectRatio: cropRatio,
          autoCrop: false,
          movable: true,
          scalable: true,
          zoomable: true,
          rotatable: true,
          responsive: true,
          background: true,
        },
      })
      .use(AwsS3, {
        shouldUseMultipart: false,
        async getUploadParameters(file) {
          try {
            const result = await getS3FileUploadUrl(
              file.name!,
              usePrivateUpload ? FileAccessTier.Secured : FileAccessTier.Public,
            );
            const uploadUrl = result?.uploadUrl;

            if (!usePrivateUpload && !result?.cdnUrl) {
              const e = new Error(`No CDN URL returned for ${file.name}`);
              captureError(e, {
                extra: {
                  fileName: file.name,
                },
              });
            }
            const newFilename = result?.newFileName!;
            uppy.setFileMeta(file.id, { name: newFilename });
            uppy.setFileState(file.id, { name: newFilename });

            if (!uploadUrl) {
              const e = new Error(
                `No pre-signed URL returned for ${file.name}`,
              );
              captureError(e, {
                extra: {
                  fileName: file.name,
                },
              });

              // Show error message in Uppy UI
              uppy.info(
                `Failed to prepare upload for ${file.name}. Please try again.`,
                "error",
                5000,
              );
              uppy.removeFile(file.id);

              // Return a rejected promise to trigger Uppy's error handling
              return Promise.reject(e);
            }

            return {
              method: "PUT",
              url: uploadUrl,
              headers: {
                "Content-Type": file.type,
              },
            };
          } catch (error) {
            console.error(
              `Failed to get pre-signed URL for ${file.name}`,
              error,
            );
            captureError(error);

            // Show error message in Uppy UI
            const errorMessage =
              error instanceof Error
                ? error.message
                : `Failed to upload ${file.name}. Please try again.`;
            uppy.info(errorMessage, "error", 5000);

            // Remove the file and let Uppy handle the error state
            uppy.removeFile(file.id);

            // Return a rejected promise to trigger Uppy's error handling
            return Promise.reject(error);
          }
        },
      });

    if (
      (isMobileOrTablet && allowedSources === "webcam") ||
      (!isMobileOrTablet &&
        (allowedSources === "webcam" || allowedSources === "all")) // web
    ) {
      const isVideoOnly =
        allowedFileTypes?.length &&
        allowedFileTypes.every(t => t.startsWith("video/"));
      instance.use(Webcam, {
        ...(isVideoOnly ? { modes: ["video-audio", "video-only"] } : {}),
      });
    }

    return instance;
  });

  // Cleanup on unmount: stop webcam and close Uppy instance
  React.useEffect(() => {
    return () => {
      const webcam = uppy.getPlugin<Webcam<any, any>>("Webcam");
      if (webcam && typeof webcam.stop === "function") {
        webcam.stop();
      }
      if (typeof (uppy as any).close === "function") {
        (uppy as any).close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useUppyEvent(uppy, "complete", async (result: any) => {
    // Stop the webcam stream
    const webcam = uppy.getPlugin<Webcam<any, any>>("Webcam");
    if (webcam && typeof webcam.stop === "function") {
      webcam.stop();
    }

    if (result?.successful?.length > 0 && onFilesUploaded) {
      await onFilesUploaded(result.successful);
    }

    if (result?.failed?.length === 0) {
      if (onSuccess) {
        onSuccess();
      }
    }
  });

  useUppyEvent(uppy, "upload-error", (file: any, error: any) => {
    console.log("UppyComponent upload-error", { file, error });
    const errorMessage =
      error?.message ||
      `Failed to upload ${file?.name || "file"}. Please try again.`;
    uppy.info(errorMessage, "error", 5000);
  });

  useUppyEvent(uppy, "error", (error: any) => {
    const errorMessage =
      error?.message || "An error occurred during upload. Please try again.";
    uppy.info(errorMessage, "error", 5000);
  });

  useUppyEvent(uppy, "file-added", async (file: OsUppyFile) => {
    try {
      validateFileSize(file);
    } catch (error: any) {
      uppy.info(error.message, "error", 5000); // Show error in Uppy UI for 5 seconds
      uppy.removeFile(file.id); // Remove the invalid file
      return;
    }

    if (file && onFileAdded) {
      await onFileAdded(file, uppy);
    }
  });

  return (
    <DashboardReactComponent
      uppy={uppy}
      width={width}
      height={height}
      proudlyDisplayPoweredByUppy={false}
      // we don't want to show the native camera buttons if we're using the webcam plugin
      // since that allows the back camera to be used
      showNativePhotoCameraButton={
        isMobileOrTablet && allowedSources !== "webcam"
      }
      showNativeVideoCameraButton={
        isMobileOrTablet && allowedSources !== "webcam"
      }
      // disable local files if we're only allowing webcam
      disableLocalFiles={allowedSources === "webcam"}
    />
  );
};

function validateFileSize(file: OsUppyFile) {
  if (file.type.startsWith("image/")) {
    if (file.size && file.size > 10 * 1024 * 1024) {
      throw new Error(`File size exceeds the maximum allowed size of 10 MB`);
    }
  } else if (file.type.startsWith("video/")) {
    if (file.size && file.size > 1000 * 1024 * 1024) {
      throw new Error(`File size exceeds the maximum allowed size of 1000 MB`);
    }
  } else if (file.size && file.size > 50 * 1024 * 1024) {
    throw new Error(`File size exceeds the maximum allowed size of 50 MB`);
  }
}
